import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Dumbbell, Zap, ShieldCheck, ChevronRight, Check } from "lucide-react";
import { TRAINING_PLAN } from "../data/trainingPlan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionTotal = (session) => {
  const groups = session.blocks || session.parts || [];
  return groups.reduce((t, g) => t + g.exercises.reduce((s, e) => s + (e.sets || 1), 0), 0);
};
const phaseTotal = (phase) => {
  const list = TRAINING_PLAN[phase].routines || TRAINING_PLAN[phase].split || [];
  return list.reduce((t, s) => t + sessionTotal(s), 0);
};
const IN_TOTAL = phaseTotal("inSeason");
const OFF_TOTAL = phaseTotal("offSeason");

// Read-only when no token; interactive per-set check-off when a player token is passed.
export default function TrainingPlans({ accent = "#C41E24", secondary = "#D4AF37", token = null }) {
  const [phase, setPhase] = useState("inSeason");
  const [gender, setGender] = useState("boys");
  const [completed, setCompleted] = useState([]);
  const data = TRAINING_PLAN[phase];
  const interactive = !!token;

  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/training/progress`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setCompleted(data.completed || []))
      .catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    if (!token) return;
    const optimistic = completed.includes(key) ? completed.filter((c) => c !== key) : [...completed, key];
    setCompleted(optimistic);
    try {
      const { data } = await axios.post(`${API}/training/progress/toggle`, { set_id: key },
        { headers: { Authorization: `Bearer ${token}` } });
      setCompleted(data.completed || []);
    } catch (e) { load(); }
  };
  const isDone = (key) => completed.includes(key);

  const { total, done, pct } = useMemo(() => {
    const prefix = phase === "inSeason" ? "is_" : "os_";
    const t = phase === "inSeason" ? IN_TOTAL : OFF_TOTAL;
    const d = completed.filter((c) => c.startsWith(prefix)).length;
    return { total: t, done: d, pct: t ? Math.round((d / t) * 100) : 0 };
  }, [completed, phase]);

  const SetPills = ({ sessionId, exIdx, sets }) => (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
      {Array.from({ length: sets }).map((_, si) => {
        const key = `${sessionId}:${exIdx}:${si}`;
        const done = isDone(key);
        return (
          <button key={si} data-testid={`set-${sessionId}-${exIdx}-${si}`} onClick={() => toggle(key)}
            title={`Set ${si + 1}`}
            className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold transition-all"
            style={done ? { background: "#22C55E", color: "#000" } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.15)" }}>
            {done ? <Check size={13} /> : si + 1}
          </button>
        );
      })}
    </div>
  );

  const Exercise = ({ e, sessionId, exIdx }) => (
    <li className="flex items-start gap-2 text-sm">
      <ChevronRight size={14} className="mt-1 shrink-0" style={{ color: accent }} />
      <div className="flex-1">
        <div><span className="text-white/85 font-semibold">{e.name}</span> <span className="text-white/45">— {e.detail}</span>{!interactive && <span className="text-white/35"> · {e.sets} {e.sets === 1 ? "set" : "sets"}</span>}</div>
        {interactive && <SetPills sessionId={sessionId} exIdx={exIdx} sets={e.sets || 1} />}
      </div>
    </li>
  );

  // running exercise index per session (across blocks/parts)
  const renderGroups = (session, groups) => {
    let ei = -1;
    return groups.map((g, gi) => (
      <div key={gi} className="mb-3 last:mb-0">
        <div className="text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: session.blocks ? "rgba(255,255,255,0.6)" : secondary }}>{g.name}</div>
        <ul className="space-y-2">
          {g.exercises.map((e, k) => { ei += 1; return <Exercise key={k} e={e} sessionId={session.id} exIdx={ei} />; })}
        </ul>
      </div>
    ));
  };

  return (
    <div data-testid="training-plans">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex items-center gap-1 bg-black/40 border border-white/10 rounded-full p-1">
          {[["inSeason", "In-Season"], ["offSeason", "Off-Season"]].map(([k, label]) => (
            <button key={k} data-testid={`plan-phase-${k}`} onClick={() => setPhase(k)}
              className="px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all"
              style={{ background: phase === k ? accent : "transparent", color: phase === k ? "#fff" : "rgba(255,255,255,0.5)" }}>
              {label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 bg-black/40 border border-white/10 rounded-full p-1">
          {[["boys", "Boys"], ["girls", "Girls"]].map(([k, label]) => (
            <button key={k} data-testid={`plan-gender-${k}`} onClick={() => setGender(k)}
              className="px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all"
              style={{ background: gender === k ? secondary : "transparent", color: gender === k ? "#000" : "rgba(255,255,255,0.5)" }}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-white/50 ml-auto">
          <CalendarDays size={14} style={{ color: secondary }} />
          <span className="font-semibold text-white/70">{data.seasons[gender]}</span>
        </div>
      </div>

      {/* Per-phase completion bar (player mode) */}
      {interactive && (
        <div className="rounded-xl border border-white/10 bg-[#141414] p-4 mb-5" data-testid="training-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/60">{data.label} Plan Completion</span>
            <span className="font-cover text-lg" style={{ color: pct >= 100 ? "#22C55E" : secondary }} data-testid="training-progress-pct">{pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-black/50 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: pct >= 100 ? "#22C55E" : accent }} />
          </div>
          <p className="text-[11px] text-white/40 mt-2">{done} of {total} sets completed — check off each set as you finish it. In-Season &amp; Off-Season track separately.</p>
        </div>
      )}

      <div className="rounded-xl border p-4 mb-6" style={{ borderColor: `${accent}44`, background: `${accent}0d` }}>
        <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: secondary }}>Primary Goal</div>
        <p className="text-sm text-white/70">{data.goal}</p>
      </div>

      {phase === "inSeason" ? (
        <>
          <div className="flex items-center gap-2 mb-3"><CalendarDays size={16} style={{ color: accent }} /><h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">Weekly Micro-Cycle</h4></div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-7" data-testid="plan-microcycle">
            {data.microCycle.map((d) => (
              <div key={d.day} className="rounded-lg border border-white/10 bg-[#141414] p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: secondary }}>{d.day}</div>
                <div className="text-xs text-white/70 leading-snug">{d.focus}</div>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            {data.routines.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-xl border border-white/10 bg-[#141414] p-5">
                <div className="flex items-center gap-2 mb-1"><Dumbbell size={16} style={{ color: accent }} /><h5 className="font-subheadings text-base font-bold uppercase tracking-wide">{r.title}</h5></div>
                <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: secondary }}>{r.when}</div>
                {r.note && <p className="text-xs text-white/45 italic mb-3">{r.note}</p>}
                {renderGroups(r, r.blocks)}
              </motion.div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3"><Zap size={16} style={{ color: accent }} /><h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">4-Day Training Split</h4></div>
          <div className="grid lg:grid-cols-2 gap-5" data-testid="plan-split">
            {data.split.map((d, i) => (
              <motion.div key={d.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-xl border border-white/10 bg-[#141414] p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="font-cover text-lg px-2.5 py-0.5 rounded" style={{ background: accent, color: "#fff" }}>{d.day}</span>
                  <h5 className="font-subheadings text-sm font-bold uppercase tracking-wide leading-tight">{d.title}</h5>
                </div>
                {renderGroups(d, d.parts)}
              </motion.div>
            ))}
          </div>
        </>
      )}

      <div className="mt-6 flex items-start gap-2 text-xs text-white/45 rounded-lg border border-white/10 bg-[#0d0d0d] p-4">
        <ShieldCheck size={16} className="text-[#22C55E] shrink-0 mt-0.5" />
        <span>Injury-Prevention Guarantee — Nordics, Copenhagens &amp; Single-Leg RDLs are built in to protect your ACL/hamstrings through the postseason. Next O is a supplemental high-performance partner, not a replacement for team practices.</span>
      </div>
    </div>
  );
}
